/**
 * Part Registry
 * Manages part factories for creating parts by name
 */

import type { IPart, PartFactory } from './types';

/**
 * Registry for part factories
 * Parts register themselves here to be instantiated by name
 */
export class PartRegistry {
  private factories = new Map<string, PartFactory>();

  /**
   * Register a factory for creating parts with the given name
   */
  register(name: string, factory: PartFactory): void {
    if (this.factories.has(name)) {
      console.warn(`PartRegistry: Overwriting existing factory for "${name}"`);
    }
    this.factories.set(name, factory);
  }

  /**
   * Create a part instance with the given name and parameters
   * @returns The created part, or null if no factory is registered
   */
  create(name: string, params: Record<string, unknown>): IPart | null {
    const factory = this.factories.get(name);
    if (!factory) {
      // Not all parts need runtime implementation - some are data-only
      return null;
    }
    return factory(params);
  }

  /**
   * Check if a factory is registered for the given part name
   */
  has(name: string): boolean {
    return this.factories.has(name);
  }

  /**
   * Get all registered part names
   */
  getRegisteredNames(): string[] {
    return Array.from(this.factories.keys());
  }
}
